import React, { useState } from 'react';
import { Box, Typography, CircularProgress, TextField, IconButton, Tooltip, LinearProgress, Select, MenuItem, Button, Popover, List, ListItem, ListItemIcon, ListItemSecondaryAction, Checkbox, FormControlLabel } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

export const TablePagination = ({
  count,
  page, // 0-indexed
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  enableColumnVisibility = false,
  columnOrder = [],
  hiddenCols = new Set(),
  pinnedCols = new Set(),
  toggleCol,
  togglePin,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  labels = {},
  hiddenFields = [],
  colorMode,
  setColorMode
}) => {
  const [draggedKey, setDraggedKey] = useState(null);
  const totalPages = Math.ceil(count / rowsPerPage);
  const [jumpPage, setJumpPage] = useState('');

  const handlePageChange = (newPage) => {
    const p = Math.max(0, Math.min(newPage, totalPages - 1));
    onPageChange(p);
  };

  const execJump = () => {
    const val = parseInt(jumpPage, 10);
    if (!isNaN(val) && val > 0 && val <= totalPages) {
      handlePageChange(val - 1);
      setJumpPage('');
    }
  };

  const handleJumpPage = (e) => {
    if (e.key === 'Enter') execJump();
  };

  const startRecord = count === 0 ? 0 : page * rowsPerPage + 1;
  const endRecord = Math.min((page + 1) * rowsPerPage, count);
  const progress = (count > 0) ? ((page + 1) / totalPages) * 100 : 0;

  const [anchorCols, setAnchorCols] = useState(null);

  return (
    <Box sx={{ 
      position: 'relative',
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      border: '1px solid #cbd5e1',
      margin: '2px 4px 2px auto',
      zIndex: 10,
      px: 0.6,
      py: 0.1,
      height: 24,
      minHeight: 24,
      overflow: 'hidden'
    }}>
      
      {/* Filas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', fontWeight: 700, color: '#94a3b8' }}>FILAS:</Typography>
        <Select
          value={rowsPerPage}
          onChange={(e) => { onRowsPerPageChange(parseInt(e.target.value, 10)); }}
          variant="standard"
          disableUnderline
          disabled={loading}
          MenuProps={{ disableRestoreFocus: true }}
          sx={{ 
            fontSize: 'clamp(0.58rem, 0.56rem + 0.10vw, 0.70rem)', fontWeight: 700, color: '#0f172a', width: 42,
            bgcolor: '#f8fafc', borderRadius: '4px', textAlign: 'center'
          }}
        >
          {[10, 25, 50, 100, 250, 500, 5000].map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.10vw, 0.70rem)', fontWeight: 600 }}>
              {option === 5000 ? 'Todos' : option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Rango Miniaturizado */}
      <Box sx={{ borderLeft: '1px solid #f1f5f9', ml: 1, pl: 1, minWidth: 70 }}>
        {loading ? <CircularProgress size={10} thickness={6} sx={{ color: '#0655A5' }} /> : (
          <Typography sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.10vw, 0.74rem)', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
            {startRecord}-{endRecord} <span style={{ color: '#cbd5e1', fontWeight: 400 }}>/</span> {count}
          </Typography>
        )}
      </Box>

      {/* Ir a página - Súper compacto */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, borderLeft: '1px solid #f1f5f9', ml: 1, pl: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#eff6ff', borderRadius: '6px', px: 0.5, py: 0.1, border: '1px solid #dbeafe' }}>
          <TextField
            size="small"
            value={jumpPage}
            onChange={(e) => setJumpPage(e.target.value)}
            onKeyDown={handleJumpPage}
            placeholder={`${page + 1}`}
            variant="standard"
            InputProps={{ 
              disableUnderline: true,
              startAdornment: <EditIcon sx={{ fontSize: '0.65rem', color: '#3b82f6', mr: 0.2 }} />
            }}
            sx={{ width: 35, '& .MuiInputBase-input': { fontSize: 'clamp(0.58rem, 0.56rem + 0.10vw, 0.70rem)', fontWeight: 700, textAlign: 'center', p: 0, color: '#0655A5' } }}
          />
          <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.08vw, 0.68rem)', fontWeight: 600, color: '#3b82f6' }}>/ {totalPages}</Typography>
        </Box>
      </Box>

      {/* Controles de Navegación Pegados */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, borderLeft: '1px solid #f1f5f9', ml: 0.8, pl: 0.3 }}>
        <IconButton size="small" onClick={() => handlePageChange(0)} disabled={page === 0 || loading} sx={{ p: 0.2, fontSize: '1rem' }}><FirstPageIcon fontSize="inherit" /></IconButton>
        <IconButton size="small" onClick={() => handlePageChange(page - 1)} disabled={page === 0 || loading} sx={{ p: 0.2, fontSize: '1rem' }}><NavigateBeforeIcon fontSize="inherit" /></IconButton>
        <IconButton size="small" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages - 1 || loading} sx={{ p: 0.2, fontSize: '1rem' }}><NavigateNextIcon fontSize="inherit" /></IconButton>
        <IconButton size="small" onClick={() => handlePageChange(totalPages - 1)} disabled={page >= totalPages - 1 || loading} sx={{ p: 0.2, fontSize: '1rem' }}><LastPageIcon fontSize="inherit" /></IconButton>
      </Box>

      {/* Barra de Progreso - Línea base casi invisible */}
      <Tooltip title={`Progreso: ${Math.round(progress)}%`} arrow placement="top">
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2.5, cursor: 'help' }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: '100%', bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': { backgroundColor: '#0655A5' }
            }} 
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
